import { z as zod } from "zod";
import { useForm } from "react-hook-form";
import { useBoolean } from "minimal-shared/hooks";
import { zodResolver } from "@hookform/resolvers/zod";

import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import LoadingButton from "@mui/lab/LoadingButton";
import InputAdornment from "@mui/material/InputAdornment";

import { useRouter } from "src/routes/hooks";

import { Iconify } from "src/components/iconify";
import { Form, Field } from "src/components/hook-form";

import { FormHead } from "../../components/form-head";
import { toast } from "sonner";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "src/store/store";
import { doLogin } from "src/store/auth/auth.action";
import { useEffect } from "react";

// ----------------------------------------------------------------------

export type SignInSchemaType = zod.infer<typeof SignInSchema>;

export const SignInSchema = zod.object({
  email: zod
    .string()
    .min(1, { message: "Email is required!" })
    .email({ message: "Email must be a valid email address!" })
    .trim(),
  password: zod
    .string()
    .min(1, { message: "Password is required!" })
    .min(6, { message: "Password must be at least 6 characters!" })
    .trim()
    .refine((val) => val.trim().length > 0, {
      message: "Please enter a valid password!",
    }),
});

// ----------------------------------------------------------------------

export function JwtSignInView() {
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();

  const showPassword = useBoolean();

  const { isAuthenticated, token } = useSelector(
    (state: RootState) => state.auth
  );

  useEffect(() => {
    if (isAuthenticated && token) {
      router.replace("/dashboard");
    }
  }, [isAuthenticated, token, router]);

  const defaultValues: SignInSchemaType = {
    email: "",
    password: "",
  };

  const methods = useForm<SignInSchemaType>({
    resolver: zodResolver(SignInSchema),
    defaultValues,
    mode: "onChange",
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const onSubmit = handleSubmit(async (data) => {
    try {
      const response = await dispatch(doLogin(data));

      if (doLogin.fulfilled.match(response)) {
        toast.success(response.payload.message);
        if (response?.payload?.data?.authentication?.accessToken) {
          router.push("/dashboard");
        }
      } else if (doLogin.rejected.match(response)) {
        const errorMessage =
          (response.payload as string) || "Something went wrong!";
        toast.error(errorMessage);
      }
    } catch (error) {
      console.log(error);
      const errorMessage =
        typeof error === "string"
          ? error
          : error instanceof Error
            ? error.message
            : "Something went wrong!";
      toast.error(errorMessage);
    }
  });

  const renderForm = () => (
    <Box sx={{ gap: 3, display: "flex", flexDirection: "column" }}>
      <Field.Text name="email" label="Email address" />

      <Box sx={{ gap: 1.5, display: "flex", flexDirection: "column" }}>
        <Field.Text
          name="password"
          label="Password"
          placeholder="6+ characters"
          type={showPassword.value ? "text" : "password"}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={showPassword.onToggle} edge="end">
                    <Iconify
                      icon={
                        showPassword.value
                          ? "solar:eye-bold"
                          : "solar:eye-closed-bold"
                      }
                    />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
      </Box>

      <LoadingButton
        fullWidth
        color="primary"
        size="large"
        type="submit"
        variant="contained"
        loading={isSubmitting}
        loadingIndicator="Log in..."
      >
        Log in
      </LoadingButton>
    </Box>
  );

  return (
    <>
      <FormHead
        title="Log in to your account"
        sx={{ textAlign: { xs: "center", md: "left" } }}
      />

      <Form methods={methods} onSubmit={onSubmit}>
        {renderForm()}
      </Form>
    </>
  );
}
